import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, CheckCircle2, AlertCircle, FileText, Loader2, Pill,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEP_WELCOME = "welcome";
const STEP_UPLOAD  = "upload";
const STEP_SUCCESS = "success";
const STEP_EXPIRED = "expired";
const STEP_ERROR   = "error";

export default function TerapiaLinkPortal() {
  const { token } = useParams();
  const [step, setStep] = useState(STEP_WELCOME);
  const [linkData, setLinkData] = useState(null);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [mittente, setMittente] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/deblistering/public/terapia-link/${token}`);
        if (r.data.documento_ricevuto) {
          setLinkData(r.data);
          setStep(STEP_SUCCESS);
        } else {
          setLinkData(r.data);
          setStep(STEP_WELCOME);
        }
      } catch (err) {
        const status = err.response?.status;
        if (status === 410) setStep(STEP_EXPIRED);
        else if (status === 404) setStep(STEP_EXPIRED);
        else setStep(STEP_ERROR);
      }
    })();
  }, [token]);

  function handleFileSelect(e) {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }

  async function handleUpload() {
    if (!file) { toast.error("Seleziona un file prima di inviare"); return; }
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Formato non supportato. Usa PDF, JPG, PNG o WebP.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File troppo grande. Massimo 20 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("note", note);
      fd.append("mittente_nome", mittente);
      await axios.post(`${API_URL}/api/deblistering/public/terapia-link/${token}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStep(STEP_SUCCESS);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 409) {
        setStep(STEP_SUCCESS);
      } else {
        toast.error(detail || "Errore durante l'invio. Riprova.");
      }
    } finally {
      setUploading(false);
    }
  }

  // ======= RENDER =======

  if (step === STEP_EXPIRED) return <FullPageMessage icon={AlertCircle} iconClass="text-red-400" title="Link non valido o scaduto" body="Questo link non è più disponibile. Contatta direttamente la struttura per assistenza." />;
  if (step === STEP_ERROR)   return <FullPageMessage icon={AlertCircle} iconClass="text-slate-400" title="Errore di caricamento" body="Non è stato possibile caricare i dati. Riprova più tardi." />;
  if (!linkData)             return <FullPageMessage icon={Loader2} iconClass="text-violet-400 animate-spin" title="Caricamento in corso..." />;

  if (step === STEP_SUCCESS) return (
    <PortalShell struttura={linkData.struttura_nome}>
      <div className="flex flex-col items-center text-center gap-4 py-6">
        <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-500" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900">Documento ricevuto</h2>
        <p className="text-slate-500 text-sm max-w-sm">
          Grazie! La struttura ha ricevuto il documento e lo elaborerà al più presto.
          Non è necessario fare altro.
        </p>
        <div className="mt-2 bg-emerald-50 border border-emerald-100 rounded-xl px-5 py-3 text-sm text-emerald-700">
          Ospite: <strong>{linkData.ospite_nome}</strong> — {linkData.tipologia_label}
        </div>
      </div>
    </PortalShell>
  );

  return (
    <PortalShell struttura={linkData.struttura_nome}>
      {/* Intestazione richiesta */}
      <div className="mb-6 bg-violet-50 border border-violet-100 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center shrink-0">
            <Pill className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">{linkData.ospite_nome}</p>
            <p className="text-sm text-violet-600">{linkData.tipologia_label}</p>
          </div>
        </div>
        {linkData.messaggio && (
          <p className="mt-3 text-sm text-slate-600 border-t border-violet-100 pt-3 italic">
            "{linkData.messaggio}"
          </p>
        )}
      </div>

      {step === STEP_WELCOME && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            La struttura <strong>{linkData.struttura_nome}</strong> Le richiede di caricare il documento
            relativo alla <strong>{linkData.tipologia_label?.toLowerCase()}</strong>.
          </p>
          <p className="text-sm text-slate-500">
            Formati accettati: <strong>PDF, JPG, PNG, WebP</strong> — max 20 MB.
          </p>
          <button
            type="button"
            onClick={() => setStep(STEP_UPLOAD)}
            className="w-full py-3 rounded-xl bg-violet-600 text-white font-semibold text-sm hover:bg-violet-700 transition"
          >
            Carica documento
          </button>
        </div>
      )}

      {step === STEP_UPLOAD && (
        <div className="space-y-4">
          {/* Mittente */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Il suo nome (opzionale)</label>
            <input
              type="text"
              value={mittente}
              onChange={e => setMittente(e.target.value)}
              placeholder="Es. Dott. Mario Rossi"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            />
          </div>

          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed px-6 py-10 flex flex-col items-center gap-3 transition ${
              dragOver ? "border-violet-500 bg-violet-50" :
              file ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-violet-400 hover:bg-violet-50/30"
            }`}
          >
            {file ? (
              <>
                <FileText className="w-10 h-10 text-emerald-500" />
                <p className="text-sm font-semibold text-emerald-700">{file.name}</p>
                <p className="text-xs text-emerald-500">{(file.size / 1024).toFixed(0)} KB — {file.type}</p>
                <span className="text-xs text-slate-400">Tocca per cambiare file</span>
              </>
            ) : (
              <>
                <Upload className="w-10 h-10 text-slate-400" />
                <p className="text-sm font-semibold text-slate-700">Trascina il file qui</p>
                <p className="text-xs text-slate-400">oppure tocca per selezionarlo</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Note aggiuntive (opzionale)</label>
            <textarea
              rows={3}
              value={note}
              onChange={e => setNote(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 resize-none"
              placeholder="Es. Terapia aggiornata il 15/06/2026..."
            />
          </div>

          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full py-3 rounded-xl bg-violet-600 text-white font-semibold text-sm hover:bg-violet-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Invio in corso..." : "Invia documento"}
          </button>
        </div>
      )}
    </PortalShell>
  );
}

function PortalShell({ struttura, children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-violet-500 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
              <Pill className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-base leading-tight">Affidya</p>
              {struttura && <p className="text-violet-200 text-xs">{struttura}</p>}
            </div>
          </div>
        </div>
        {/* Body */}
        <div className="p-6">{children}</div>
        {/* Footer */}
        <div className="px-6 pb-4 text-center">
          <p className="text-[11px] text-slate-400">
            Link sicuro generato da Affidya · I dati sono protetti e trasmessi in modo cifrato.
          </p>
        </div>
      </div>
    </div>
  );
}

function FullPageMessage({ icon: Icon, iconClass = "text-slate-400", title, body }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8 flex flex-col items-center text-center gap-4">
        <Icon className={`w-12 h-12 ${iconClass}`} />
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {body && <p className="text-sm text-slate-500">{body}</p>}
      </div>
    </div>
  );
}
